import { useState } from "react";
import { Link, useParams } from "react-router";
import { useQuery, useMutation } from "@apollo/client";
import {
  Archive,
  ArchiveRestore,
  Calendar,
  Check,
  Inbox,
  Loader2,
  Mail,
  MapPin,
  Phone,
  Video,
} from "lucide-react";
import {
  Avatar,
  Badge,
  BookingStateBadge,
  Card,
  ClientName,
  clientDisplayName,
  clientInitials,
  EmptyState,
  LeadStateBadge,
  PageHeader,
  TriageControl,
} from "../components/ui";
import { useToastPush } from "../components/ui/Toast";
import {
  GET_LEAD_DETAIL,
  GET_LEAD_BOOKING,
  GET_LEAD_AVAILABILITY,
  GET_LEAD_CUSTOM_URL,
  ARCHIVE_LEADS,
} from "../apollo/leads";
import { GET_BOOKING_TYPES } from "../apollo/sessions";
import {
  formatDateLabel,
  formatDuration,
  formatWhen,
  methodToLocation,
  timeAgo,
} from "../utils/sessionTime";

// ── Types for the query shape ────────────────────────────────────────

interface LeadCustomer {
  id: string;
  name: string;
  alias: string | null;
  email: string | null;
  phoneNumber: string | null;
  isPrimary: boolean;
  businessName: string | null;
  title: string | null;
}

interface LeadDetailData {
  id: string;
  status: string;
  source: string | null;
  triageStatus: string;
  situation: string | null;
  method: string | null;
  bookingTypeId: string | null;
  setupIntentId: string | null;
  availabilitySlotId: string | null;
  customUrlId: string | null;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
  questions: {
    isTimeSensitive: string | null;
    isInvolvingTheCourt: string | null;
    isPaperworkAvailable: string | null;
  } | null;
  // JSON string of { label: answer } from lead_form submissions (the API
  // serializes the form_data JSON column as a string; parse before rendering).
  formData: string | null;
  customers: LeadCustomer[];
}

// ── Helpers ──────────────────────────────────────────────────────────

function humanize(s: string): string {
  return s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function MethodIcon({ method, size = 12 }: { method: string; size?: number }) {
  if (method === "in-person") return <MapPin size={size} strokeWidth={2} />;
  if (method === "video") return <Video size={size} strokeWidth={2} />;
  if (method === "phone") return <Phone size={size} strokeWidth={2} />;
  return null;
}

function KvRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 py-2 first:pt-0 last:pb-0">
      <span className="text-[12px] text-ink-4">{label}</span>
      <span className="text-[12.5px] font-medium text-right flex items-center gap-1.5 min-w-0">
        {children}
      </span>
    </div>
  );
}

const STATUS_ORDER = ["new", "qualified", "booked"] as const;

// ── Component ────────────────────────────────────────────────────────

export function LeadDetail() {
  const { id } = useParams<{ id: string }>();
  const toast = useToastPush();
  const [archiveConfirmOpen, setArchiveConfirmOpen] = useState(false);

  const { data, loading } = useQuery(GET_LEAD_DETAIL, {
    variables: { id },
    skip: !id,
  });
  const lead: LeadDetailData | null = data?.Lead ?? null;

  // The mutation response (id + archivedAt) updates the normalized cache, so
  // the page flips to the archived/restored state without a refetch.
  const [archiveLeadsMut, { loading: archiveLoading }] = useMutation(ARCHIVE_LEADS);

  const handleArchive = async () => {
    if (!lead) return;
    setArchiveConfirmOpen(false);
    try {
      await archiveLeadsMut({ variables: { ids: [lead.id], archive: true } });
      toast({ kind: "success", title: "Lead archived" });
    } catch {
      toast({ kind: "error", title: "Failed to archive lead" });
    }
  };

  const handleRestore = async () => {
    if (!lead) return;
    try {
      await archiveLeadsMut({ variables: { ids: [lead.id], archive: false } });
      toast({ kind: "success", title: "Lead restored" });
    } catch {
      toast({ kind: "error", title: "Failed to restore lead" });
    }
  };

  const { data: typeData } = useQuery(GET_BOOKING_TYPES, {
    variables: { perPage: 1000, sortField: "name", sortOrder: "ASC" },
  });

  const { data: bookingData } = useQuery(GET_LEAD_BOOKING, {
    variables: { filter: { leadId: id } },
    skip: !id || lead?.status !== "booked",
  });

  const { data: availData } = useQuery(GET_LEAD_AVAILABILITY, {
    variables: { id: lead?.availabilitySlotId },
    skip: !lead?.availabilitySlotId,
  });

  const { data: customUrlData } = useQuery(GET_LEAD_CUSTOM_URL, {
    variables: { id: lead?.customUrlId },
    skip: !lead?.customUrlId,
  });

  // Loading state
  if (loading && !lead) {
    return (
      <>
        <PageHeader heading={false} back={{ to: "/leads", label: "leads" }} />
        <div className="px-4 md:px-7 pb-20">
        <div className="animate-pulse space-y-6">
          <div className="h-10 w-96 rounded bg-canvas" />
          <div className="h-5 w-64 rounded bg-canvas" />
          <div className="grid grid-cols-12 gap-6">
            <div className="col-span-8 space-y-3">
              <div className="h-48 rounded-lg bg-canvas" />
              <div className="h-32 rounded-lg bg-canvas" />
            </div>
            <div className="col-span-4 space-y-3">
              <div className="h-40 rounded-lg bg-canvas" />
              <div className="h-32 rounded-lg bg-canvas" />
            </div>
          </div>
        </div>
        </div>
      </>
    );
  }

  // Not found
  if (!lead) {
    return (
      <>
        <PageHeader heading={false} back={{ to: "/leads", label: "leads" }} />
        <div className="px-4 md:px-7 pb-20">
        <div className="bg-paper-hi rounded-lg shadow-card">
          <EmptyState
            icon={<Inbox size={28} strokeWidth={1.5} />}
            heading="Lead not found"
            description="This lead may have been removed, or the link is out of date."
          />
        </div>
        </div>
      </>
    );
  }

  const bookingTypes: { id: string; name: string }[] = typeData?.allBookingTypes ?? [];
  const typeName = lead.bookingTypeId
    ? (bookingTypes.find((t) => t.id === lead.bookingTypeId)?.name ?? null)
    : null;

  const booking = bookingData?.allBookings?.[0] ?? null;
  const avail = availData?.Availability ?? null;
  const customUrl = customUrlData?.CustomUrl ?? null;

  const primary = lead.customers.find((c) => c.isPrimary) ?? lead.customers[0] ?? null;

  // form_data arrives as a JSON string; parse it into { label: answer } pairs.
  const formAnswers: [string, string][] = (() => {
    if (!lead.formData) return [];
    try {
      const parsed = JSON.parse(lead.formData) as Record<string, string>;
      return Object.entries(parsed);
    } catch {
      return [];
    }
  })();

  // createdAt is a naive-UTC ISO timestamp; split into the (date, time) parts
  // the sessionTime helpers expect.
  const [receivedDate = "", receivedClock = ""] = lead.createdAt.split("T");

  // Questions rendering (same entries as the session detail client brief)
  const questions = lead.questions;
  const questionEntries: { label: string; value: string }[] = [];
  if (questions) {
    if (questions.isTimeSensitive)
      questionEntries.push({ label: "Time sensitive", value: humanize(questions.isTimeSensitive) });
    if (questions.isInvolvingTheCourt)
      questionEntries.push({ label: "Involving the court", value: humanize(questions.isInvolvingTheCourt) });
    if (questions.isPaperworkAvailable)
      questionEntries.push({ label: "Paperwork available", value: humanize(questions.isPaperworkAvailable) });
  }

  // Lifecycle timeline
  const statusIdx = Math.max(0, STATUS_ORDER.indexOf(lead.status as (typeof STATUS_ORDER)[number]));
  const steps = [
    {
      key: "new",
      title: "Enquiry received",
      sub: formatWhen(receivedDate, receivedClock),
    },
    {
      key: "qualified",
      title: "Qualified",
      sub:
        statusIdx >= 1
          ? "Situation and contacts provided"
          : "Waiting for the client to share details",
    },
    {
      key: "booked",
      title: "Session booked",
      sub:
        statusIdx >= 2
          ? "Converted to a session"
          : lead.setupIntentId
            ? "Payment method saved — awaiting booking"
            : "Awaiting booking",
    },
  ];

  return (
    <div>
      {/* The lead's name is the hero h1 below, so this row carries the back
          link and the archive action only. */}
      <PageHeader
        heading={false}
        back={{ to: "/leads", label: "leads" }}
        actions={
          lead.archivedAt ? (
            <button
              type="button"
              onClick={handleRestore}
              disabled={archiveLoading}
              className="px-3 py-1.5 rounded-md bg-paper-hi text-ink-2 border hair hover:bg-paper-2 text-[12.5px] flex items-center gap-1.5 transition-colors disabled:opacity-50"
            >
              {archiveLoading ? (
                <Loader2 size={12} strokeWidth={1.8} className="animate-spin" />
              ) : (
                <ArchiveRestore size={12} strokeWidth={1.8} />
              )}
              Restore
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setArchiveConfirmOpen(true)}
              disabled={archiveLoading}
              className="px-3 py-1.5 rounded-md bg-paper-hi border hair hover:bg-paper-2 text-[12.5px] flex items-center gap-1.5 transition-colors text-ink-4 hover:text-rust disabled:opacity-50"
            >
              <Archive size={12} strokeWidth={1.8} />
              Archive
            </button>
          )
        }
      />

      <div className="px-4 md:px-7 pb-6">
        {/* Hero */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
          <div className="lg:col-span-8">
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <LeadStateBadge status={lead.status} />
              <TriageControl leadId={lead.id} value={lead.triageStatus} size="md" />
              {lead.archivedAt && <Badge>Archived</Badge>}
            </div>
            <h1 className="font-display text-[40px] font-medium leading-[1.05] tracking-tight">
              {primary ? clientDisplayName(primary) : "Lead"}
            </h1>
            <div className="mt-3 text-[13.5px] text-ink-3 flex items-center gap-2 flex-wrap">
              {typeName && <span>{typeName}</span>}
              {typeName && lead.method && <span className="text-ink-6">·</span>}
              {lead.method && <span>{methodToLocation(lead.method)}</span>}
              {(typeName || lead.method) && lead.customers.length > 0 && (
                <span className="text-ink-6">·</span>
              )}
              {lead.customers.length > 0 && (
                <span>
                  {lead.customers.length} contact{lead.customers.length !== 1 ? "s" : ""}
                </span>
              )}
            </div>
          </div>

          <div className="lg:col-span-4">
            <div className="bg-paper-hi rounded-lg shadow-card p-5">
              <div className="text-ink-4 text-[10.5px] uppercase tracking-wider font-medium">
                Received
              </div>
              <div className="font-display text-[28px] font-medium leading-none mt-1 num">
                {formatWhen(receivedDate, receivedClock)}
              </div>
              <div className="text-[12.5px] text-ink-3 mt-1">
                {formatDateLabel(receivedDate)} · {timeAgo(lead.createdAt)}
              </div>
              <div className="bg-paper rounded p-3 mt-4">
                <div className="text-[10.5px] text-ink-4 uppercase tracking-wider font-medium">
                  Requested time
                </div>
                {avail ? (
                  <div className="text-[13px] font-medium mt-1 flex items-center gap-1.5">
                    <Calendar size={12} strokeWidth={2} />
                    {formatWhen(avail.date, avail.startTime)} ·{" "}
                    {formatDuration(avail.startTime, avail.endTime, avail.date)}
                  </div>
                ) : (
                  <div className="text-[13px] text-ink-4 mt-1">No time selected yet</div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-7 py-2 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
          {/* Main column */}
          <div className="lg:col-span-8 space-y-3">
            {/* Situation */}
            <Card header={{ title: "Their situation" }}>
              <div className="p-5">
                {lead.situation ? (
                  <p className="text-[13.5px] leading-relaxed text-ink-2 whitespace-pre-line">
                    {lead.situation}
                  </p>
                ) : (
                  <p className="text-[13px] text-ink-4">
                    The client hasn't described their situation yet.
                  </p>
                )}
              </div>
            </Card>

            {/* Client brief */}
            {questionEntries.length > 0 && (
              <Card
                header={{
                  title: "Client brief",
                  action: <span className="text-[11px] text-ink-4">From intake form</span>,
                }}
              >
                <div className="p-5">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {questionEntries.map((q) => (
                      <div key={q.label}>
                        <div className="text-[11px] text-ink-4 uppercase tracking-wider font-medium mb-1">
                          {q.label}
                        </div>
                        <div className="text-[13px] font-medium">{q.value}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </Card>
            )}

            {/* Form answers (lead_form submissions) */}
            {formAnswers.length > 0 && (
              <Card
                header={{
                  title: "Form answers",
                  action: <span className="text-[11px] text-ink-4">From website form</span>,
                }}
              >
                <div className="p-5">
                  <div className="space-y-3">
                    {formAnswers.map(([label, value]) => (
                      <div key={label}>
                        <div className="text-[11px] text-ink-4 uppercase tracking-wider font-medium mb-1">
                          {label}
                        </div>
                        <div className="text-[13px] font-medium whitespace-pre-line">{value}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </Card>
            )}

            {/* Contacts */}
            {lead.customers.length > 0 && (
              <Card
                header={{
                  title: "Contacts",
                  meta: `${lead.customers.length} contact${lead.customers.length !== 1 ? "s" : ""}`,
                }}
              >
                <div className="divide-hair">
                  {lead.customers.map((c) => (
                    <div key={c.id} className="px-5 py-4 flex items-start gap-3">
                      <Avatar initials={clientInitials(c)} size="lg" />
                      <div className="min-w-0">
                        <div className="text-[13.5px] font-medium flex items-center gap-2 flex-wrap">
                          <ClientName customer={c} />
                          {c.isPrimary && (
                            <span className="text-[10px] font-semibold uppercase tracking-wider bg-tide-ss text-tide-d rounded px-1.5 py-0.5">
                              Primary
                            </span>
                          )}
                        </div>
                        {(c.title || c.businessName) && (
                          <div className="text-[12px] text-ink-3 mt-0.5">
                            {[c.title, c.businessName].filter(Boolean).join(" · ")}
                          </div>
                        )}
                        <div className="mt-1.5 space-y-0.5">
                          {c.email && (
                            <div className="text-[12.5px] text-ink-4 flex items-center gap-1.5">
                              <Mail size={11} strokeWidth={2} />
                              {c.email}
                            </div>
                          )}
                          {c.phoneNumber && (
                            <div className="text-[12.5px] text-ink-4 flex items-center gap-1.5 num">
                              <Phone size={11} strokeWidth={2} />
                              {c.phoneNumber}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <aside className="lg:col-span-4 space-y-3">
            {/* Progress */}
            <Card header={{ title: "Progress" }}>
              <div className="p-5">
                <div className="space-y-0">
                  {steps.map((step, i) => {
                    const done = i < statusIdx;
                    const current = i === statusIdx;
                    return (
                      <div key={step.key} className="relative pl-6 pb-5 last:pb-0">
                        {i < steps.length - 1 && (
                          <span
                            className={`absolute left-[5px] top-4 bottom-0 w-0.5 ${
                              done ? "bg-tide-s" : "bg-canvas-2"
                            }`}
                          />
                        )}
                        <span
                          className={`absolute left-0 top-1 w-3 h-3 rounded-full border-2 ${
                            done
                              ? "bg-tide border-tide"
                              : current
                                ? "bg-spark border-spark ring-4 ring-spark-s"
                                : "bg-paper-hi border-ink-6"
                          }`}
                        />
                        <div
                          className={`text-[13px] ${
                            done || current ? "font-medium text-ink" : "text-ink-4"
                          }`}
                        >
                          {step.title}
                        </div>
                        <div className="text-[11.5px] text-ink-4 mt-0.5">{step.sub}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </Card>

            {/* Consultation */}
            <Card header={{ title: "Consultation" }}>
              <div className="px-5 py-4 divide-hair">
                <KvRow label="Source">
                  {lead.source === "lead_form" ? "Website form" : lead.source === "booking_funnel" ? "Booking funnel" : lead.source ?? "—"}
                </KvRow>
                <KvRow label="Type">{typeName ?? "—"}</KvRow>
                <KvRow label="Method">
                  {lead.method ? (
                    <>
                      <MethodIcon method={lead.method} />
                      {methodToLocation(lead.method)}
                    </>
                  ) : (
                    "—"
                  )}
                </KvRow>
                <KvRow label="Requested time">
                  {avail ? (
                    <span className="num">{formatWhen(avail.date, avail.startTime)}</span>
                  ) : (
                    "—"
                  )}
                </KvRow>
                <KvRow label="Payment method">
                  {lead.setupIntentId ? (
                    <>
                      <Check size={12} strokeWidth={2.5} className="text-sage" />
                      Card on file
                    </>
                  ) : (
                    "Not saved"
                  )}
                </KvRow>
              </div>
            </Card>

            {/* Source */}
            {customUrl && (
              <Card header={{ title: "Source" }}>
                <div className="px-5 py-4 divide-hair">
                  <KvRow label="Campaign link">
                    <span className="font-mono text-[11.5px] bg-paper hair border rounded px-1.5 py-0.5 truncate">
                      {customUrl.slug}
                    </span>
                  </KvRow>
                  <KvRow label="First touch">
                    <span className="num">{formatDateLabel(receivedDate)}</span>
                  </KvRow>
                </div>
              </Card>
            )}

            {/* Related */}
            <Card header={{ title: "Related" }}>
              {booking ? (
                <Link
                  to={`/sessions/${booking.id}`}
                  className="px-5 py-4 flex items-center gap-3 hover:bg-paper-2 transition"
                >
                  <div className="w-9 h-9 rounded bg-tide-ss text-tide flex items-center justify-center flex-shrink-0">
                    <Calendar size={14} strokeWidth={2} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-medium truncate">
                      {booking.bookingType?.name ?? "Session"}
                    </div>
                    <div className="text-[11.5px] text-ink-4 num">
                      {booking.availability
                        ? formatWhen(booking.availability.date, booking.availability.startTime)
                        : "No time set"}
                    </div>
                  </div>
                  <BookingStateBadge state={booking.state} />
                </Link>
              ) : (
                <div className="px-5 py-4 text-[12.5px] text-ink-4 leading-relaxed">
                  No session yet. When this lead books, their session will be linked here.
                </div>
              )}
            </Card>
          </aside>
        </div>
      </div>

      {/* Archive confirmation modal */}
      {archiveConfirmOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
        >
          <div
            className="absolute inset-0 bg-ink/40 backdrop-blur-sm"
            onClick={() => setArchiveConfirmOpen(false)}
          />
          <div className="relative bg-paper-hi rounded-lg shadow-lift max-w-sm w-full p-5">
            <h2 className="text-[15px] font-semibold">
              Archive {primary ? clientDisplayName(primary) : "this lead"}?
            </h2>
            <p className="text-[13px] text-ink-3 mt-2 leading-relaxed">
              The lead will be hidden from your active leads list. You can
              restore it from the Archived view at any time.
            </p>
            <div className="flex items-center justify-end gap-2 mt-5">
              <button
                type="button"
                onClick={() => setArchiveConfirmOpen(false)}
                className="px-3 py-1.5 rounded-md text-ink-3 hover:bg-paper-2 text-[12.5px] transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleArchive}
                disabled={archiveLoading}
                className="px-3.5 py-1.5 rounded-md bg-rust text-paper-hi text-[12.5px] font-medium hover:bg-rust/90 transition-colors flex items-center gap-1.5 disabled:opacity-50"
              >
                {archiveLoading && (
                  <Loader2 size={12} strokeWidth={2} className="animate-spin" />
                )}
                Archive
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
